import { memo, useMemo, useState } from 'react';
import { version } from '../../package.json';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, NavPill } from '@werkstatt/ui';
import {
  LayoutDashboard,
  Calendar,
  CalendarDays,
  Car,
  Users,
  FileText,
  Receipt,
  Settings,
  Menu,
  Shield,
  Building2,
  Mail,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { useAuthStore } from '../store/authStore';

export interface Props {
  className?: string;
}

interface NavItem {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
  roles?: string[];
}

export const Navigation = memo(({ className }: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();

  const [collapsed, setCollapsed] = useState(false);

  const navItems = useMemo(() => {
    const items: NavItem[] = [];

    // Dashboard (for all users)
    items.push({
      title: 'Dashboard',
      icon: LayoutDashboard,
      path: '/dashboard',
    });

    // Main features
    items.push({
      title: 'Kalender',
      icon: CalendarDays,
      path: '/calendar',
    });

    items.push({
      title: 'Fahrzeuge',
      icon: Car,
      path: '/vehicles',
    });

    items.push({
      title: 'Kunden',
      icon: Users,
      path: '/customers',
    });

    items.push({
      title: 'Buchungen',
      icon: Calendar,
      path: '/bookings',
    });

    items.push({
      title: 'Verträge',
      icon: FileText,
      path: '/contracts',
    });

    items.push({
      title: 'Rechnungen',
      icon: Receipt,
      path: '/invoices',
    });

    items.push({
      title: 'Postfach',
      icon: Mail,
      path: '/mailbox',
    });

    // Admin-only items
    if (user?.role === 'admin') {
      items.push({
        title: 'Einstellungen',
        icon: Settings,
        path: '/settings',
        roles: ['admin'],
      });
      items.push({
        title: 'Mein Mandant',
        icon: Building2,
        path: '/my-tenant',
        roles: ['admin'],
      });
    }

    return items;
  }, [user?.role]);

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  return (
    <div
      className={cn(
        'h-full transition-all duration-300 overflow-hidden rounded-card',
        'backdrop-blur-lg shadow-glass',
        'bg-[rgb(var(--sphere-card)_/_var(--sphere-card-alpha))]',
        'border border-[rgb(var(--sphere-border)_/_var(--sphere-border-alpha))]',
        collapsed ? 'w-20' : 'w-64',
        className
      )}
    >
      <div className="flex flex-col justify-between h-full p-3">
        {/* Header / Toggle */}
        <div className="flex items-center justify-between px-1 py-2 mb-2">
          {!collapsed && (
            <div className="flex items-center space-x-2">
              {user?.role === 'super_admin'
                ? <Shield className="h-6 w-6 text-primary" />
                : <Car className="h-6 w-6 text-primary" />}
              <span className="font-display font-semibold text-foreground">
                {user?.role === 'super_admin' ? 'Superadmin' : 'Mietwagenverwaltung'}
              </span>
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="p-1"
            onClick={() => setCollapsed(!collapsed)}
          >
            <Menu className="h-5 w-5 text-foreground" />
          </Button>
        </div>

        {/* Navigation Items */}
        <nav className="flex-1 flex flex-col space-y-1">
          {navItems.map((item) => (
            <NavPill
              key={item.path}
              icon={item.icon}
              label={item.title}
              active={isActive(item.path)}
              collapsed={collapsed}
              onClick={() => navigate(item.path)}
            />
          ))}
        </nav>

        {/* Version */}
        {!collapsed && (
          <div className="px-3 py-3 text-xs text-muted-foreground border-t border-[rgb(var(--sphere-border)_/_var(--sphere-border-alpha))]">
            App-Version: {version}
          </div>
        )}
      </div>
    </div>
  );
});

Navigation.displayName = 'Navigation';
